import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  LoadingScreen,
  Separator,
  Wordmark,
} from "@roberttamas/ui";
import { FirebaseError } from "firebase/app";
import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { GoogleLogo } from "@/components/icons";
import { useAuth } from "@/lib/auth";
import { authErrorMessage } from "@/lib/errors";

interface LocationState {
  from?: { pathname: string };
}

type Mode = "signin" | "signup";

export function LoginPage() {
  const { t } = useTranslation();
  const { user, loading, signInWithGoogle, signInWithEmail, signUpWithEmail, resetPassword } =
    useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // The landing's buy flow deep-links to /login?mode=signup for new buyers.
  const [mode, setMode] = useState<Mode>(
    searchParams.get("mode") === "signup" ? "signup" : "signin",
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);

  const from = (location.state as LocationState | null)?.from?.pathname ?? "/sites";

  useEffect(() => {
    if (!loading && user) navigate(from, { replace: true });
  }, [loading, user, navigate, from]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (user) return <Navigate to={from} replace />;

  const handleEmailSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (pending) return;
    setPending(true);
    try {
      if (mode === "signup") {
        await signUpWithEmail(email.trim(), password);
        toast.success(t("login.accountCreated"));
      } else {
        await signInWithEmail(email.trim(), password);
      }
      // Redirect is handled by the effect once `user` updates.
    } catch (err) {
      toast.error(authErrorMessage(err));
    } finally {
      setPending(false);
    }
  };

  const handleGoogle = async () => {
    if (pending) return;
    setPending(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      if (
        err instanceof FirebaseError &&
        (err.code === "auth/popup-closed-by-user" || err.code === "auth/cancelled-popup-request")
      ) {
        // User dismissed the popup — nothing worth surfacing.
      } else {
        toast.error(authErrorMessage(err));
      }
    } finally {
      setPending(false);
    }
  };

  const handleReset = async () => {
    const target = email.trim();
    if (!target) {
      toast.error(t("login.resetNeedEmail"));
      return;
    }
    if (pending) return;
    setPending(true);
    try {
      await resetPassword(target);
      toast.success(t("login.resetSent", { email: target }));
    } catch (err) {
      toast.error(authErrorMessage(err));
    } finally {
      setPending(false);
    }
  };

  const busyLabel = mode === "signup" ? t("login.creatingAccount") : t("login.signingIn");
  const idleLabel = mode === "signup" ? t("login.createAccount") : t("login.signIn");

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <CardTitle>
            <Wordmark strong="raw" rest=".gallery" className="text-4xl" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={(e) => void handleEmailSubmit(e)} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="email">{t("login.emailLabel")}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder={t("login.emailPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={pending}
                required
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t("login.passwordLabel")}</Label>
                {mode === "signin" && (
                  <button
                    type="button"
                    onClick={() => void handleReset()}
                    disabled={pending}
                    className="text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline disabled:opacity-50"
                  >
                    {t("login.forgotPassword")}
                  </button>
                )}
              </div>
              <Input
                id="password"
                type="password"
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
                placeholder={mode === "signup" ? t("login.passwordPlaceholderNew") : "••••••••"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={pending}
                required
                minLength={6}
              />
            </div>
            <Button type="submit" className="w-full" disabled={pending}>
              {pending ? busyLabel : idleLabel}
            </Button>
          </form>

          <div className="my-4 flex items-center gap-3">
            <Separator className="flex-1" />
            <span className="text-xs uppercase text-muted-foreground">{t("login.or")}</span>
            <Separator className="flex-1" />
          </div>

          <Button
            className="w-full"
            variant="outline"
            onClick={() => void handleGoogle()}
            disabled={pending}
          >
            <GoogleLogo />
            {t("login.continueWithGoogle")}
          </Button>

          <p className="mt-4 text-center text-sm text-muted-foreground">
            {mode === "signup" ? t("login.haveAccount") : t("login.noAccount")}{" "}
            <button
              type="button"
              onClick={() => setMode(mode === "signup" ? "signin" : "signup")}
              disabled={pending}
              className="font-medium text-foreground underline-offset-4 hover:underline disabled:opacity-50"
            >
              {mode === "signup" ? t("login.signIn") : t("login.createOne")}
            </button>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

